import { logger } from "../../debug";
import { getConsentMap, loadScript } from "../../generalUtils";
import { getRegion, isAPAC, isEMEA, isNA, isQuebec } from "../../generalUtils/envFns";
import { intlConversionIdsByLocale, intlManagerConversionIdsByRegion, namConversionIdsByLocale, namManagerConversionId } from "./const";
import { getGoogleAdsConversionLabel, googleAdsEvent } from "./utils";

export interface GoogleAdsConfig {
    awId?: string;
    mccAwId?: string;
    dataLayerName?: string;
    enabled?: boolean;
    name?: string;
}

function consentModeIsEnabled() {
  // Consent mode is disabled for all APAC regions (including KR)
  return !isAPAC();
}

export default (config: GoogleAdsConfig) => {
    // Plugin implementation here
    let adsScriptLoading: ReturnType<typeof loadScript>;
    let mccAdsScriptLoading: ReturnType<typeof loadScript>;
    const { awId, mccAwId, dataLayerName, enabled } = config;
    
    const region = getRegion();
    let googlePath = "https://www.googletagmanager.com/";
    if (region === "ca" || region === "us") {
        googlePath = `/metrics/${region}/`;
    }
    let awIdToUse = awId;
    let mccAwIdToUse = mccAwId;
    const regionToUse = region === "us" ? "default" : region;

    if (awIdToUse === undefined) {
        awIdToUse = `AW-${isNA() 
        ? namConversionIdsByLocale[regionToUse]
        : intlConversionIdsByLocale[regionToUse]}`;
    }

    if (mccAwIdToUse === undefined) {
        mccAwIdToUse = `AW-${isNA() 
        ? namManagerConversionId
        : intlManagerConversionIdsByRegion[regionToUse]}`;
    }

    const dataLayerToUse = dataLayerName ?? "dataLayer";

    function gtag(...args) {
        if (window[dataLayerToUse] === undefined) {
            window[dataLayerToUse] = [];
        }

        window[dataLayerToUse].push(arguments);
        logger.debug(`Google Ads(${awId}) ${dataLayerToUse} event`, args);
    }

    let hasConsent = false;

    const updateConsent = (
        updateType: "default" | "update",
        consented: boolean = false
    ) => {
        if (consentModeIsEnabled()) {
            hasConsent = true;
            logger.debug("APAC region detected - skpping consent mode");
            return;
        }

        const level = consented ? "granted" : "denied";
        const consentParams = {
            ad_user_data: level,
            ad_storage: level,
            ad_personalization: level,
            analytics_storage: level,
            wait_for_update: updateType === "default" ? 5000 : undefined,
        };
        gtag("consent", updateType, consentParams);
        hasConsent = consented;
    }

    const advancedConsentModeEnabled = isEMEA() || isAPAC() || isNA();

    return {
        config,
        name: "google-ads",
        enabled: enabled ?? false,
        loaded() {
            return adsScriptLoading?.isReady() ?? false;
            // return true;
        },
        ready() {
            logger.debug(`Google Ads(${awId}) Plugin is ready`);
        },
        track({ payload}) {
            const { properties, event } = payload;
            if (
                event === "automatedData" &&
                properties.validationResult.success === true &&
                properties.validationResult.data.payload.event.eventName
            ) {
                const automatedEventName = properties.validationResult.data.payload.event.eventName;
                logger.debug(
                    `Google Ads(${awIdToUse}) Plugin handling event ${automatedEventName}`,
                    properties
                );

                if (mccAwIdToUse) {
                    logger.debug(
                        `Google Ads(${mccAwIdToUse}) Plugin handling event ${automatedEventName}`,
                        properties
                    );
                }

                // We'll likely have to add more later

                return googleAdsEvent(
                    automatedEventName,
                    properties.validationResult.data,
                    (eventType, eventName, eventParams) => {
                        const { accConversionLabel, managerAccConversionLabel } = getGoogleAdsConversionLabel(getRegion(), automatedEventName);

                        if (awIdToUse !== undefined && accConversionLabel !== undefined) {
                            gtag(eventType, eventName, { ...eventParams, "send_to": `${awIdToUse}/${accConversionLabel}`});
                        }
                        if (mccAwIdToUse !== undefined &&managerAccConversionLabel !== undefined) {
                            gtag(eventType, eventName, { ...eventParams, "send_to": `${mccAwIdToUse}/${managerAccConversionLabel}`});
                        }
                    }
                )
            }
        },
        initialize() {
            logger.debug("Google Ads Plugin is loading");
            adsScriptLoading = loadScript(
                `${googlePath}gtag/js?id=${awIdToUse}${dataLayerName !== undefined ? `&l=${dataLayerName}` : ""}`
            );
            if (mccAwIdToUse) {
                mccAdsScriptLoading = loadScript(
                    `${googlePath}gtag/js?id=${mccAwIdToUse}${dataLayerName !== undefined ? `&l=${dataLayerName}` : ""}`
                );
            }
            window[dataLayerToUse] = window[dataLayerToUse] || [];

            const { C0004 } = getConsentMap();
            let consented = false;
            const region = getRegion();
            if (region === "us" || (region === "ca" && !isQuebec())) {
                consented =
                    !navigator?.["globalPrivacyControl"] &&
                    (typeof C0004 === "undefined" || Number(C0004) === 1)
            }
            updateConsent("default", consented);

            gtag("config", awIdToUse, { send_page_view: false});
            adsScriptLoading.promise.then(() => {
                logger.debug("Google Ads script loaded");
            });

            if (mccAdsScriptLoading) {
                gtag("config", mccAwIdToUse, {
                    send_page_view: false
                });
                mccAdsScriptLoading.promise.then(() => {
                    logger.debug("MCC Google Ads script loaded");
                });
            }
        },
        consentUpdate({ payload }) {
            const { C0004 } = payload;
            let consented = Number(C0004) === 1;
            const region = getRegion();
            if (region === "us" || (region === "ca" && !isQuebec())) {
                consented = typeof C0004 === "undefined" || consented;
            }
            updateConsent("update", consented);
        }
    }
};